/* ==========================================================================
   SM-CARD-VARIANT-PICKER — Sale Madrid
   Added: 17 Aug 2026
 
   Makes the colour swatches on a collection card act as the variant picker,
   so the card button can be "Add to cart" instead of "Choose options".
 
   Only runs on cards that Liquid decided are safe for direct add
   (single option, that option is the swatch option, no quantity rules).
   Those cards contain a real <form> with input.product-variant-id. Cards that
   still open the Choose options modal have no such input, so this script
   ignores them completely.
 
   Behaviour:
     - tap a swatch  -> hidden variant id input is rewritten
                     -> that swatch becomes the active one
                     -> card image swaps to the variant image if there is one
                     -> button disables and reads Sold out for unavailable colours
     - the first swatch is already marked .active by Liquid, so the default is
       the first variant with no JS needed. If JS never loads, the form still
       submits that first variant rather than failing.
 
   Deliberately NOT in global.js — keeping custom JS in its own file avoids the
   syntax-error-breaks-everything problem.
   ========================================================================== */
(function () {
  'use strict';
 
  /* ----------------------------------------------------------------------
     KILL SWITCH for the card image swap.
 
     Set to false if selecting a colour makes the card image render at the
     wrong size. Everything else keeps working: the swatch still selects the
     variant, Add to cart still adds the right colour, sold-out still
     disables. Only the picture stops changing.
 
     component-card.css:186 sets  .card__media .media img { width:100%;
     height:100%; object-fit:cover }  inside an absolutely positioned box, so
     changing src should NOT be able to resize the image. If it still does,
     something else on the card is involved and this flag makes it a one-word
     fix rather than a redeploy.
     ---------------------------------------------------------------------- */
  var SWAP_IMAGE = true;
 
  var SWATCH = '.card__swatch .swatch-item';
  var FORM_INPUT = 'input.product-variant-id';
 
  /* Find the add-to-cart form belonging to the same card as this swatch. */
  function cardOf(el) {
    return el.closest('.card-wrapper, .card--block, .product-grid__item, .card');
  }
 
  function priceEl(card) {
    return card.querySelector('.card--block-price, .price, .card__price');
  }
 
  function setSoldOut(card, soldOut) {
    var btn = card.querySelector('.add-to-cart-button, button[name="add"]');
    if (!btn) return;
    var label = btn.querySelector('[data-button-text], .add-to-cart-text__content, span');
    if (soldOut) {
      btn.setAttribute('disabled', 'disabled');
      btn.dataset.smSoldOut = 'true';
      if (label && !btn.dataset.smLabel) {
        btn.dataset.smLabel = label.textContent.trim();
        label.textContent = btn.dataset.smSoldOutText || 'Sold out';
      }
    } else {
      btn.removeAttribute('disabled');
      if (label && btn.dataset.smLabel) {
        label.textContent = btn.dataset.smLabel;
        delete btn.dataset.smLabel;
      }
      delete btn.dataset.smSoldOut;
    }
  }
 
  /* ------------------------------------------------------------------------
     Selecting the PRODUCT images only.
 
     Measured on the live store: .media contains THREE <img> elements —
 
       [0]  95x95   position:static    object-fit:contain   <- Fordeer badge
       [1]  198x198 position:absolute  object-fit:cover     <- main product image
       [2]  198x198 position:absolute  object-fit:cover     <- secondary/hover
 
     Fordeer injects its "BUY 2 GET 1 FREE" badge as the FIRST img inside
     .media. Taking querySelectorAll('img')[0] therefore painted the variant
     photo into the badge's 95x95 contain box (the "image became so small"
     bug) and hid the real product image.
 
     card-product-media.liquid renders both product images with
     class: 'motion-reduce' (lines 48 and 55), which the badge does not have,
     so that class is the reliable filter. The position:absolute fallback
     covers the case where a theme update drops the class.
     ------------------------------------------------------------------------ */
  function productImages(card) {
    var box = card.querySelector('.card__media, card-media, .card--block-media');
    if (!box) return [];
    var imgs = [].slice.call(box.querySelectorAll('img.motion-reduce'));
    if (imgs.length) return imgs;
    /* Fallback: product images are the absolutely positioned, cover-fitted
       ones. Anything static (an injected badge) is excluded. */
    return [].slice.call(box.querySelectorAll('img')).filter(function (im) {
      var cs = getComputedStyle(im);
      return cs.position === 'absolute' && cs.objectFit === 'cover';
    });
  }
 
  function swapImage(card, src) {
    if (!SWAP_IMAGE) return;
    var imgs = productImages(card);
    if (!imgs.length) return;
 
    /* Cards render up to two images: the main one and a secondary "hover"
       image (card-product-media.liquid line 55). Only the main image has a
       per-variant equivalent, so swapping just that one would leave the hover
       image showing the PREVIOUS colour. Hide the secondary image once a
       colour has been picked, and restore it if we return to the default. */
    var secondary = imgs.length > 1 ? imgs[1] : null;
 
    if (!src) {
      /* Variant has no image assigned in Shopify admin - leave the card as is
         rather than blanking it. */
      return;
    }
 
    var main = imgs[0];
    if (!main.dataset.smOriginalSrc) {
      main.dataset.smOriginalSrc = main.getAttribute('src') || '';
      main.dataset.smOriginalSrcset = main.getAttribute('srcset') || '';
    }
 
    /* Do NOT remove srcset and do NOT touch width/height/sizes. The card image
       is absolutely positioned with object-fit:cover and takes its box from the
       wrapper, but stripping srcset while leaving `sizes` in place left the
       browser without a matching candidate and the image rendered at the wrong
       size. Replacing srcset with a single candidate keeps src and srcset
       consistent, and every other attribute is left exactly as Liquid wrote it. */
    main.setAttribute('srcset', src + ' 1200w');
    main.setAttribute('src', src);
 
    if (secondary) secondary.style.visibility = 'hidden';
  }
 
  function restoreImage(card) {
    if (!SWAP_IMAGE) return;
    var imgs = productImages(card);
    if (!imgs.length) return;
    var main = imgs[0];
    if (main.dataset.smOriginalSrc) {
      main.src = main.dataset.smOriginalSrc;
      if (main.dataset.smOriginalSrcset) main.setAttribute('srcset', main.dataset.smOriginalSrcset);
    }
    if (imgs.length > 1) imgs[1].style.visibility = '';
  }
 
  function selectSwatch(item) {
    var card = cardOf(item);
    if (!card) return;
 
    var input = card.querySelector(FORM_INPUT);
    /* No hidden input means this card still uses the Choose options modal.
       Leave it alone so the modal keeps working. */
    if (!input) return;
 
    var span = item.querySelector('[data-variant-id]') || item;
    var id = span.getAttribute('data-variant-id');
    if (!id) return;
 
    var available = span.getAttribute('data-variant-available') !== 'false';
 
    input.value = id;
    /* Shopify ignores a disabled input on submit, so keep it enabled only
       while the chosen variant is actually purchasable. */
    if (available) {
      input.removeAttribute('disabled');
    } else {
      input.setAttribute('disabled', 'disabled');
    }
 
    card.querySelectorAll(SWATCH).forEach(function (s) {
      s.classList.toggle('active', s === item);
    });
 
    swapImage(card, span.getAttribute('data-variant-img'));
    /* Returning to the first swatch restores the card's original media,
       including the hover image. */
    var items = card.querySelectorAll(SWATCH);
    if (items.length && items[0] === item) restoreImage(card);
    setSoldOut(card, !available);
  }
 
  /* Delegated: works for swatches revealed later by the +N expander and for
     cards injected by pagination, filtering, or section re-renders. */
  function onClick(event) {
    var item = event.target.closest(SWATCH);
    if (!item) return;
    var card = cardOf(item);
    if (!card || !card.querySelector(FORM_INPUT)) return; /* modal card: ignore */
    event.preventDefault();
    event.stopPropagation();
    selectSwatch(item);
  }
 
  function onKey(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var item = event.target.closest(SWATCH);
    if (!item) return;
    var card = cardOf(item);
    if (!card || !card.querySelector(FORM_INPUT)) return;
    event.preventDefault();
    selectSwatch(item);
  }
 
  /* ------------------------------------------------------------------------
     ADD TO CART REDIRECTING TO /cart
 
     product-form.js does this:
 
       init() {
         this.form = this.querySelector('form');
         if (!this.form) return;                    <-- no submit listener
         this.form.addEventListener('submit', ...)
       }
 
     and init() runs from the CONSTRUCTOR. halo-popup.liquid loads the script
     with document.createElement('script') + .defer = true, but a dynamically
     created script is async by default and .defer has no effect on it. So it
     can execute mid-parse, upgrading any <product-form-component> parsed so
     far before that element's <form> child exists. init() bails, no submit
     listener is attached, and the form posts natively to /cart/add — which
     makes the browser navigate to /cart instead of opening the drawer.
 
     Before the card picker this branch only rendered for single-variant
     products, so it was rare. Now it renders for every colour product.
 
     Fix: once the DOM is parsed, replace each card's product-form-component
     with a clone of itself. The clone is upgraded on insertion, this time with
     its children present, so init() completes and the submit listener binds.
     Replacing the element also discards the old one and its listeners, so this
     cannot double-bind and cannot add an item twice.
     ------------------------------------------------------------------------ */
  function repairCardForms() {
    if (!window.customElements || !customElements.get('product-form-component')) return;
 
    document.querySelectorAll('product-form-component').forEach(function (el) {
      if (el.dataset.smRepaired === 'true') return;
 
      /* Only cards. Never touch the product page or the quick-add modal. */
      if (!cardOf(el)) return;
      var form = el.querySelector('form');
      if (!form) return;                 /* nothing to repair yet */
      if (el.smBound === true) return;
 
      var clone = el.cloneNode(true);
      clone.dataset.smRepaired = 'true';
      el.replaceWith(clone);
    });
  }
 
  /* Run once the parser is done, and again once product-form.js has defined
     the element (whichever happens last). */
  function scheduleRepair() {
    repairCardForms();
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('product-form-component')
        .then(repairCardForms)
        .catch(function () {});
    }
  }
 
 
  function markFocusable(root) {
    (root || document).querySelectorAll(SWATCH).forEach(function (item) {
      var card = cardOf(item);
      if (!card || !card.querySelector(FORM_INPUT)) return;
      if (item.hasAttribute('tabindex')) return;
      item.setAttribute('tabindex', '0');
      item.setAttribute('role', 'button');
      var span = item.querySelector('[data-variant-title]');
      if (span) item.setAttribute('aria-label', span.getAttribute('data-variant-title'));
    });
  }
 
  document.addEventListener('click', onClick, true);
  document.addEventListener('keydown', onKey, true);
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      markFocusable();
      scheduleRepair();
    });
  } else {
    markFocusable();
    scheduleRepair();
  }
 
  /* Re-mark and re-repair after filtering / pagination swaps the grid. */
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      if (records[i].addedNodes.length) {
        markFocusable();
        repairCardForms();
        return;
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
 